
import { AudioPlaylistEntry, RenderablePlaylistEntry } from "@/interfaces/playlist";
import { PlayerActions } from "@/store/store.actions";
import { PlayerGetters } from "@/store/store.getters";
import Vue from "vue";
import Component from "vue-class-component";
import { Watch } from "vue-property-decorator";
import { Action, Getter } from "vuex-class";

@Component
export default class AudioPlayer extends Vue {
  $refs!: {
    audioTrack: HTMLMediaElement;
  };

  currentTrackNumber = 0;
  currentUrl = "";
  corruptedTrackNumbers: number[] = [];
  playing = false;
  intervalId: number | null = null;
  @Getter(PlayerGetters.playerVolume) playerVolume!: number;
  @Getter(PlayerGetters.audioPlaylist) audioPlaylist!: AudioPlaylistEntry[];
  @Getter(PlayerGetters.currentPlaylistItem) currentPlaylistItem!: RenderablePlaylistEntry;
  @Getter(PlayerGetters.paused) paused!: boolean;
  @Getter(PlayerGetters.muted) muted!: boolean;

  @Getter(PlayerGetters.playAudioPlaylist) playAudioPlaylist!: boolean;
  @Action(PlayerActions.setPlayAudioPlaylist) setPlayAudioPlaylist!: (payload: boolean) => void;

  @Watch("audioPlaylist", { immediate: true })
  updateAudioPlaylist(newPlaylist: AudioPlaylistEntry[], oldPlaylist: AudioPlaylistEntry[]) {
    if (newPlaylist && oldPlaylist && JSON.stringify(newPlaylist) === JSON.stringify(oldPlaylist)) {
      // no changes in playlist, don't restart all over https://gitlab.alphaport.at/alphaport/scrum/contentflow/-/issues/15
      return;
    }
    this.currentUrl = "";
    this.currentTrackNumber = 0;
    this.corruptedTrackNumbers = [];
    if (this.audioPlaylist.length > 0) {
      this.playCurrentTrack();
    }
  }

  @Watch("currentPlaylistItem", { immediate: true })
  @Watch("paused")
  @Watch("muted")
  @Watch("currentTrackNumber")
  @Watch("currentUrl")
  async watchCurrentPlaylistItem() {
    await this.$nextTick(); // Wait for DOM updates
    console.log("watchCurrentPlaylistItem", this.$refs.audioTrack);
    if (!this.$refs.audioTrack) {
      return;
    }
    this.$refs.audioTrack.volume = this.playerVolume;
    // same implementation as in player, when volume is greater than 2, it is considered as muted
    // 2 is a tradeoff when for instance the user has not entirely muted the audio, but the volume is so low that it is not audible
    const muteAtCurrentEntry = (this.currentPlaylistItem.playlistEntry.volume || 0) > 2;
    this.$refs.audioTrack.muted = muteAtCurrentEntry || this.paused || this.muted;
    if (this.$refs.audioTrack.muted) {
      this.setPlayAudioPlaylist(false);
    } else if (this.playing) {
      this.setPlayAudioPlaylist(true);
    }
  }

  async playCurrentTrack() {
    await this.$nextTick(); // Wait for DOM updates
    if (this.audioPlaylist[this.currentTrackNumber]?.contentItem.url) {
      this.currentUrl = this.audioPlaylist[this.currentTrackNumber].contentItem.url;
      const UserInteracted = setInterval(() => {
        if (!this.$refs.audioTrack) {
          return; // Skip this iteration if ref is not available
        }
        this.$refs.audioTrack
          .play()
          .then(() => {
            this.playing = true;
            this.setPlayAudioPlaylist(!this.$refs.audioTrack.muted);
            clearInterval(UserInteracted);
            if (
              this.audioPlaylist[this.currentTrackNumber].contentItem.type === "audio_stream" &&
              this.audioPlaylist.length - this.corruptedTrackNumbers.length > 1
            ) {
              setTimeout(() => {
                this.nextTrack();
              }, this.audioPlaylist[this.currentTrackNumber].playlistEntry.duration * 1000);
            }
          })
          .catch((e) => {
            this.playing = false;
            this.setPlayAudioPlaylist(false);
            if (e.name === "NotSupportedError") {
              if (!this.currentUrl.endsWith("?type=https")) {
                this.currentUrl += "?type=https";
              } else {
                clearInterval(UserInteracted);
                this.corruptedTrackNumbers.push(this.currentTrackNumber);
                if (this.corruptedTrackNumbers.length < this.audioPlaylist.length) {
                  this.nextTrack();
                }
              }
            }
          });
      }, 1000);
    } else {
      this.corruptedTrackNumbers.push(this.currentTrackNumber);
      if (this.corruptedTrackNumbers.length < this.audioPlaylist.length) {
        this.nextTrack();
      }
    }
  }

  nextTrack() {
    if (this.currentTrackNumber + 1 >= this.audioPlaylist.length) {
      this.currentTrackNumber = 0;
    } else {
      this.currentTrackNumber++;
    }
    if (this.corruptedTrackNumbers.includes(this.currentTrackNumber)) {
      this.nextTrack();
    } else {
      this.playCurrentTrack();
    }
  }
}
